import { useMemo } from 'react';
import { Label } from '@/components/atoms/label';
import { HintTooltip } from '@/components/molecules';
import { TimePicker } from '@/components/molecules/time-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Switch } from '@/components/atoms/switch';
import { useBusinessSettings } from '@/hooks/settings/useBusinessSettings';
import { DEFAULT_BUSINESS_TIMEZONE } from '@/lib/constants/timezones';
import type { QuietHoursConfig } from '@/types/reminder-policy';

interface QuietHoursEditorProps {
  value: QuietHoursConfig | null;
  onChange: (next: QuietHoursConfig | null) => void;
  error?: string;
}

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Phoenix',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

function minutesToTime(minute: number): string {
  const safe = Math.max(0, Math.min(1439, Math.trunc(minute)));
  const hh = Math.floor(safe / 60)
    .toString()
    .padStart(2, '0');
  const mm = (safe % 60).toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

function timeToMinutes(time: string): number {
  const [hh, mm] = time.split(':').map((s) => Number.parseInt(s, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return 0;
  return Math.max(0, Math.min(1439, hh * 60 + mm));
}

const DEFAULT_QUIET_START_MINUTE = 21 * 60;
const DEFAULT_QUIET_END_MINUTE = 8 * 60;

export function QuietHoursEditor({ value, onChange, error }: QuietHoursEditorProps) {
  const enabled = value !== null;
  // NOT-125 — quiet hours default to the company timezone, not a hardcoded
  // Eastern literal, so a Denver tenant enabling them doesn't silently save an
  // ET window (which the engine then runs every reminder on). Keep the query's
  // success state too: `undefined` means both "still loading" and "this tenant
  // has no configured timezone", but only the latter may use the fallback.
  const { data: businessSettings, isSuccess: businessSettingsLoaded } = useBusinessSettings();
  const companyTz = businessSettings?.timezone || undefined;
  const defaultQuiet: QuietHoursConfig = {
    startMinute: DEFAULT_QUIET_START_MINUTE,
    endMinute: DEFAULT_QUIET_END_MINUTE,
    tz: companyTz ?? DEFAULT_BUSINESS_TIMEZONE,
  };

  // The company tz (and any tz already saved on the policy) may sit outside the
  // curated list — surface both so the Select never renders an empty trigger.
  const timezoneOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...(companyTz ? [companyTz] : []),
          ...(value?.tz ? [value.tz] : []),
          ...TIMEZONES,
        ]),
      ),
    [companyTz, value?.tz],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-medium">Quiet hours</h3>
          <HintTooltip>No messages send inside this window — they roll forward.</HintTooltip>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(next) => onChange(next ? defaultQuiet : null)}
          disabled={!enabled && !businessSettingsLoaded}
          aria-label="Enable quiet hours"
        />
      </div>

      {enabled && value && (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label>Start</Label>
            <TimePicker
              value={minutesToTime(value.startMinute)}
              onChange={(v) => onChange({ ...value, startMinute: timeToMinutes(v) })}
            />
          </div>
          <div className="space-y-1">
            <Label>End</Label>
            <TimePicker
              value={minutesToTime(value.endMinute)}
              onChange={(v) => onChange({ ...value, endMinute: timeToMinutes(v) })}
            />
          </div>
          <div className="space-y-1">
            <Label>Timezone</Label>
            <Select value={value.tz} onValueChange={(tz) => onChange({ ...value, tz })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timezoneOptions.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
